import { create } from 'zustand';
import type { Project } from '@/storage/ProjectRepository';
import type { AppDefinition } from '@/types/app';
import { chatStore, projectStore } from '@/storage/projectStorePort';

/**
 * The projects someone is building, read through the ProjectStore port.
 *
 * The port is the seam: the /me/projects service with IndexedDB as its cache in the browser
 * (platform/projectStore.server.ts), SQLite on the phone, with no screen changing either time.
 * This store is only the React-facing cache around it.
 */

/**
 * What opening `/project/:id` found.
 *
 * F18: a lookup that found nothing used to leave `currentProject` null — the same value it has
 * while still looking — so a link opened in another browser, in Instagram's in-app browser or
 * after site data was cleared said "Loading…" forever. `not_found` is the server's word that
 * there is no such app of this person's; `error` is a lookup that could not be made, which is a
 * fact about the moment and not about the app.
 */
export type ProjectLookup =
  | { id: string; state: 'loading' }
  | { id: string; state: 'found' }
  | { id: string; state: 'not_found' }
  | { id: string; state: 'error'; message: string };

export interface Projects {
  projects: Project[];
  currentProject: Project | null;
  isLoading: boolean;
  error: string | null;
  /** The last project asked for by id, and what became of it. */
  lookup: ProjectLookup | null;

  loadProjects: (ownerId: string) => Promise<void>;
  loadProject: (id: string, ownerId: string) => Promise<void>;
  createFromDefinition: (
    name: string,
    description: string,
    definition: AppDefinition,
    ownerId: string,
  ) => Promise<Project>;
  /**
   * Rejects with the store's own sentence when the change was not saved — among them, the
   * server refusing a change made from a version another browser has replaced. The latest
   * version is on screen by then, so the next attempt is made from it.
   */
  updateProject: (
    id: string,
    updates: Partial<Pick<Project, 'name' | 'description' | 'definition' | 'thumbnail' | 'is_published'>>,
    ownerId: string,
  ) => Promise<void>;
  deleteProject: (id: string, ownerId: string) => Promise<void>;
}

export const useProjects = create<Projects>((set, get) => ({
  projects: [],
  currentProject: null,
  isLoading: false,
  error: null,
  lookup: null,

  loadProjects: async (ownerId) => {
    if (!ownerId) {
      set({ projects: [], isLoading: false });
      return;
    }
    set({ isLoading: true, error: null });
    try {
      // Also uploads, once, any project that exists only in this browser (O5).
      await projectStore().claimUnowned(ownerId);
      set({ projects: await projectStore().getAll(ownerId), isLoading: false });
    } catch (e) {
      set({ isLoading: false, error: e instanceof Error ? e.message : 'Could not load your projects.' });
    }
  },

  loadProject: async (id, ownerId) => {
    set({ isLoading: true, error: null, lookup: { id, state: 'loading' } });
    // Only the answer to the lookup still wanted is kept: opening another app meanwhile makes
    // this one's "not found" about nothing on screen.
    const stillWanted = () => get().lookup?.id === id;
    try {
      const project = await projectStore().getById(id, ownerId);
      if (!stillWanted()) return;
      set({
        currentProject: project,
        isLoading: false,
        lookup: project ? { id, state: 'found' } : { id, state: 'not_found' },
      });
    } catch (e) {
      if (!stillWanted()) return;
      const message = e instanceof Error ? e.message : 'Could not open that project.';
      set({ isLoading: false, error: message, lookup: { id, state: 'error', message } });
    }
  },

  createFromDefinition: async (name, description, definition, ownerId) => {
    const project = await projectStore().create({ name, description, definition }, ownerId);
    set((s) => ({ projects: [project, ...s.projects], currentProject: project }));
    return project;
  },

  updateProject: async (id, updates, ownerId) => {
    try {
      await projectStore().update(id, updates, ownerId);
    } catch (e) {
      /*
       * Refused, and the store may now hold a newer version than the screen (a 409 from
       * /me/projects loads the other browser's). Put that on screen before passing the refusal
       * on: left showing the stale copy, the next change is made from it and refused again.
       * Best effort — the refusal is what the caller must hear.
       */
      await refreshAfterRefusal(id, ownerId);
      throw e;
    }
    const current = get().currentProject;
    set({
      projects: await projectStore().getAll(ownerId),
      currentProject: current?.id === id ? { ...current, ...updates } : current,
    });
  },

  deleteProject: async (id, ownerId) => {
    await projectStore().delete(id, ownerId);
    await chatStore().deleteByProjectId(id);
    const current = get().currentProject;
    set({
      projects: await projectStore().getAll(ownerId),
      currentProject: current?.id === id ? null : current,
    });
  },
}));

async function refreshAfterRefusal(id: string, ownerId: string): Promise<void> {
  try {
    const latest = await projectStore().getById(id, ownerId);
    const projects = await projectStore().getAll(ownerId);
    useProjects.setState((s) => ({
      projects,
      currentProject: s.currentProject?.id === id ? latest : s.currentProject,
    }));
  } catch {
    /* offline, say: the refusal already says what happened */
  }
}
